import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on page change
  useEffect(() => {
    setIsOpen(false);
    setServicesDropdownOpen(false);
  }, [location]);

  // Track scroll for subtle shadow/border transition
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Forside', path: '/' },
    {
      name: 'Ydelser',
      path: '#',
      isDropdown: true,
      children: [
        { name: 'Privat rengøring', path: '/privat-rengoering', icon: 'fa-house' },
        { name: 'Erhvervsrengøring', path: '/erhvervsrengoering', icon: 'fa-building' },
      ],
    },
    { name: 'Privat', path: '/privat-rengoering' },
    { name: 'Erhverv', path: '/erhvervsrengoering' },
    { name: 'Prisberegner', path: '/prisberegner', isHighlighted: true },
    { name: 'Om os', path: '/om-os' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-200 bg-white ${
        isScrolled ? 'shadow-md border-b border-slate-200 py-3' : 'border-b border-slate-100 py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header Row: Logo & Actions */}
        <div className="flex items-center justify-between gap-4">
          
          {/* Logo (Clean brand text without icon box) */}
          <Link
            to="/"
            className="flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 group shrink-0 transition-transform duration-200 hover:scale-105 origin-left"
            aria-label="Power-Multiservice Forside"
          >
            <span className="font-black text-[18px] xs:text-[20px] sm:text-[22px] min-[1200px]:text-[26px] tracking-tight text-slate-900 group-hover:text-black transition-colors leading-tight">
              Power<span className="text-emerald-600">-</span>Multiservice
            </span>
            <span className="text-[9px] sm:text-[10px] min-[1200px]:text-[11px] font-semibold text-slate-500 uppercase tracking-widest -mt-0.5">
              Kvalitetsrengøring
            </span>
          </Link>

          {/* Desktop Right Actions (Phone & CTA) */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            <a
              href="tel:31686764"
              className="flex items-center gap-2 text-sm font-bold text-slate-800 hover:text-blue-900 transition-colors whitespace-nowrap px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
              aria-label="Ring til Power-Multiservice på 31 68 67 64"
            >
              <span className="whitespace-nowrap font-bold">31 68 67 64</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm text-white bg-emerald-600 hover:bg-emerald-700 transition-all duration-150 active:scale-[0.98] whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
            >
              <span>Få et tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile Actions: Phone & Hamburger (Visible below 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-2">
            <a
              href="tel:31686764"
              className="inline-flex items-center justify-center p-2 text-slate-800 hover:text-black transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
              aria-label="Ring 31 68 67 64"
            >
              <i className="fa-solid fa-phone text-base" aria-hidden="true" />
            </a>

            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 text-slate-800 hover:text-black focus:outline-none transition-colors"
              aria-label={isOpen ? 'Luk menu' : 'Åbn menu'}
              aria-expanded={isOpen}
            >
              <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} aria-hidden="true" />
            </button>
          </div>

        </div>

        {/* Desktop Navigation Links Row: Placed UNDER logo, left-aligned */}
        <div className="hidden min-[1200px]:flex items-center justify-start border-t border-slate-100/90 pt-2.5 mt-2.5">
          <nav className="flex items-center space-x-2 -ml-3">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm transition-colors duration-150 ${
                  isActive
                    ? 'text-blue-950 font-bold'
                    : 'text-slate-600 hover:text-blue-950 font-medium'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Privat */}
            <NavLink
              to="/privat-rengoering"
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm transition-colors duration-150 ${
                  isActive
                    ? 'text-blue-950 font-bold'
                    : 'text-slate-600 hover:text-blue-950 font-medium'
                }`
              }
            >
              Privat
            </NavLink>

            {/* Erhverv */}
            <NavLink
              to="/erhvervsrengoering"
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm transition-colors duration-150 ${
                  isActive
                    ? 'text-blue-950 font-bold'
                    : 'text-slate-600 hover:text-blue-950 font-medium'
                }`
              }
            >
              Erhverv
            </NavLink>

            {/* Om os */}
            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm transition-colors duration-150 ${
                  isActive
                    ? 'text-blue-950 font-bold'
                    : 'text-slate-600 hover:text-blue-950 font-medium'
                }`
              }
            >
              Om os
            </NavLink>

            {/* Kontakt */}
            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm transition-colors duration-150 ${
                  isActive
                    ? 'text-blue-950 font-bold'
                    : 'text-slate-600 hover:text-blue-950 font-medium'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>
        </div>
      </div>

      {/* Mobile Drawer Menu (Visible when isOpen and viewport < 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden bg-white border-b border-slate-200 px-4 pt-3 pb-6 shadow-xl animate-in slide-in-from-top-2 duration-200">
          <nav className="flex flex-col space-y-1">
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `px-2 py-2.5 text-base transition-colors ${
                  isActive ? 'text-black font-bold' : 'text-slate-700 hover:text-black font-medium'
                }`
              }
            >
              Forside
            </NavLink>

            <NavLink
              to="/privat-rengoering"
              className={({ isActive }) =>
                `px-2 py-2.5 text-base transition-colors ${
                  isActive ? 'text-black font-bold' : 'text-slate-700 hover:text-black font-medium'
                }`
              }
            >
              Privat rengøring
            </NavLink>

            <NavLink
              to="/erhvervsrengoering"
              className={({ isActive }) =>
                `px-2 py-2.5 text-base transition-colors ${
                  isActive ? 'text-black font-bold' : 'text-slate-700 hover:text-black font-medium'
                }`
              }
            >
              Erhvervsrengøring
            </NavLink>

            <NavLink
              to="/prisberegner"
              className={({ isActive }) =>
                `px-2 py-2.5 text-base transition-colors ${
                  isActive ? 'text-black font-bold' : 'text-slate-700 hover:text-black font-medium'
                }`
              }
            >
              Prisberegner
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-2 py-2.5 text-base transition-colors ${
                  isActive ? 'text-black font-bold' : 'text-slate-700 hover:text-black font-medium'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-2 py-2.5 text-base transition-colors ${
                  isActive ? 'text-black font-bold' : 'text-slate-700 hover:text-black font-medium'
                }`
              }
            >
              Kontakt
            </NavLink>

            {/* Mobile Contact & CTA Buttons */}
            <div className="pt-4 border-t border-slate-100 flex flex-col gap-3 mt-2">
              <a
                href="tel:31686764"
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-full font-semibold text-slate-900 border border-slate-300 hover:border-black transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-black text-sm" aria-hidden="true" />
                <span className="whitespace-nowrap">31 68 67 64</span>
              </a>

              <Link
                to="/kontakt"
                className="flex items-center justify-center gap-2 py-3 px-4 rounded-full font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all text-center whitespace-nowrap"
              >
                <span>Få et tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
